/*
 * Clearance Guys — "Load more" for the collection and search grids.
 *
 * Progressive enhancement over Dawn's numbered pagination: the anchor inside
 * <cg-load-more> is a real link to page N+1, so with JS off (or if this file
 * fails) the button still works and crawlers still follow the pagination
 * markup rendered underneath.
 *
 * With JS it fetches the next page through the Section Rendering API,
 * appends the cards to the existing #product-grid, updates the counts and
 * rewrites the URL with history.replaceState (NOT pushState — facets.js
 * reads history state as { searchParams } and a state object without that
 * key breaks its popstate handler).
 *
 * It is a custom element so it re-initialises automatically when facets.js
 * replaces #ProductGridContainer after a filter or sort change.
 */
if (!customElements.get('cg-load-more')) {
  customElements.define(
    'cg-load-more',
    class CgLoadMore extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.button = this.querySelector('[data-cg-load-more-button]');
        if (!this.button) return;

        this.button.addEventListener('click', this.onClick.bind(this));
      }

      get grid() {
        return document.getElementById('product-grid');
      }

      get sectionId() {
        return this.dataset.sectionId || (this.grid && this.grid.dataset.id);
      }

      onClick(event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        if (this.loading) return;

        const nextPage = parseInt(this.dataset.nextPage, 10);
        const totalPages = parseInt(this.dataset.totalPages, 10);
        if (!nextPage || !this.grid) return;

        const params = new URLSearchParams(window.location.search);
        params.set('page', String(nextPage));

        const fetchParams = new URLSearchParams(params);
        fetchParams.set('section_id', this.sectionId);

        this.setLoading(true);

        fetch(`${window.location.pathname}?${fetchParams.toString()}`)
          .then((response) => {
            if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const incoming = parsed.getElementById('product-grid');
            if (!incoming) throw new Error('Load more: no #product-grid in response');

            const items = Array.from(incoming.children);
            const firstNew = items[0] || null;
            items.forEach((item) => {
              item.classList.remove('scroll-trigger', 'animate--slide-in');
              item.removeAttribute('data-cascade');
              this.grid.appendChild(item);
            });

            try {
              history.replaceState(
                { searchParams: params.toString() },
                '',
                `${window.location.pathname}?${params.toString()}`
              );
            } catch (e) {
              /* history is unavailable in some embedded contexts — ignore */
            }

            this.dataset.nextPage = String(nextPage + 1);
            const nextLink = parsed.querySelector('[data-cg-load-more-button]');
            if (nextLink && nextPage < totalPages) {
              this.button.setAttribute('href', nextLink.getAttribute('href') || this.button.getAttribute('href'));
            }

            this.updateCounts();

            if (nextPage >= totalPages) {
              this.hidden = true;
            }

            const focusTarget = firstNew && firstNew.querySelector('a[href]');
            if (focusTarget) {
              focusTarget.setAttribute('tabindex', '-1');
              focusTarget.focus({ preventScroll: true });
              focusTarget.removeAttribute('tabindex');
            }
          })
          .catch((error) => {
            console.error(error);
            // Fall back to a normal page load so the shopper is never stuck.
            window.location.href = this.button.getAttribute('href');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.button.classList.toggle('is-loading', isLoading);
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      updateCounts() {
        const shown = this.grid ? this.grid.children.length : 0;
        const countEl = document.getElementById('ProductCount');
        const total = parseInt((countEl && countEl.dataset.productCount) || this.dataset.total, 10) || shown;
        const remaining = Math.max(total - shown, 0);

        const showing = (this.dataset.showingTemplate || '')
          .replace('[shown]', shown)
          .replace('[total]', total);
        const remainingText = (this.dataset.remainingTemplate || '').replace('[remaining]', remaining);

        if (countEl && showing) countEl.textContent = showing;

        const showingEl = this.querySelector('[data-cg-load-more-showing]');
        if (showingEl && showing) showingEl.textContent = showing;

        const remainingEl = this.querySelector('[data-cg-load-more-remaining]');
        if (remainingEl && remainingText) remainingEl.textContent = remainingText;
      }
    }
  );
}
